/**
 * 智能客服-智能体配置
 * 后端接口: CccCallerNumberController (/api/v2/ccc/caller)
 */
import React, { useState } from 'react';
import {
  Card,
  Form,
  Row,
  Col,
  Input,
  Button,
  Select,
  Popconfirm,
  message,
  Switch
} from 'antd';
import {
  cccCallerNumberListRequest,
  deleteCccCallerNumberRequest,
  toggleCccCallerNumberRequest,
  CccCallerNumber
} from '@/service/smartCustomerServiceNumber';
import { useTable } from '@/utils/useTable';
import CommonTable from '@/components/commonTable';
import RenderEllipsisColumn from '@/components/renderEllipsisColumn';
import { formSearchGutter } from '@/constants/common';
import OperateModal from './components/operateModal';
import styles from '@/styles/pageContent.module.scss';

const SmartCustomerServiceNumber = () => {
  const [form] = Form.useForm();
  const [operateInfo, setOperateInfo] = useState<any>({}); // 表格操作的信息
  const [searchParams, setSearchParams] = useState({}); // 搜索相关的参数
  const { loading, data, getData } = useTable({
    requestFunc: cccCallerNumberListRequest,
    searchParams,
    noPageChange: true // 后端不分页，一次返回全量列表
  });

  // 搜索
  const onSearch = async (values: any) => {
    setSearchParams({ ...values });
  };

  // 重置
  const onReset = () => {
    form.resetFields();
    setSearchParams({});
  };

  // 删除主叫号码
  const deleteItem = async (id: number) => {
    const res = await deleteCccCallerNumberRequest(id);
    if (res.data?.code === 0) {
      message.success('删除成功');
      getData();
    } else {
      message.error(res.data?.msg || '删除失败');
    }
  };

  // 切换启禁用状态
  const toggleItem = async (item: CccCallerNumber) => {
    const res = await toggleCccCallerNumberRequest(item.id);
    if (res.data?.code === 0) {
      message.success(item.status === 1 ? '已禁用' : '已启用');
      getData();
    } else {
      message.error(res.data?.msg || '操作失败');
    }
  };

  // 表格列
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 80
    },
    {
      title: '智能体Id',
      dataIndex: 'aiAgentId',
      width: 160,
      ellipsis: { showTitle: false },
      render: (value: string) => <RenderEllipsisColumn text={value} />
    },
     {
      title: '智能体名称',
      dataIndex: 'name',
      width: 160,
      ellipsis: { showTitle: false },
      render: (value: string) => <RenderEllipsisColumn text={value} />
    },
    {
      title: '主叫号码',
      dataIndex: 'callers',
      width: 160,
      ellipsis: { showTitle: false },
      render: (value: string) => <RenderEllipsisColumn text={value} />
    },
    {
      title: '策略标识',
      dataIndex: 'strategyKey',
      width: 200,
      ellipsis: { showTitle: false },
      render: (value: string) => <RenderEllipsisColumn text={value} />
    },
    {
      title: '重试次数',
      dataIndex: 'tries',
      width: 200
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (value: number) =>
        value === 1 ? (
          <span className="successText">启用</span>
        ) : (
          <span className="dangerText">禁用</span>
        )
    },
    {
      title: '启禁用',
      dataIndex: 'toggle',
      width: 90,
      render: (value: any, item: CccCallerNumber) => (
        <Switch
          checked={item.status === 1}
          checkedChildren="启用"
          unCheckedChildren="禁用"
          onChange={() => toggleItem(item)}
        />
      )
    },
    {
      title: '操作',
      dataIndex: 'operate',
      width: 140,
      fixed: 'right' as const,
      render: (value: any, item: CccCallerNumber) => {
        return (
          <div className={styles.tableOperateColumn}>
            <Button
              type="link"
              onClick={() => setOperateInfo({ type: 'edit', data: item })}
            >
              编辑
            </Button>
            <Popconfirm
              title="确定要删除该智能体吗?"
              onConfirm={() => deleteItem(item.id)}
            >
              <Button type="link">删除</Button>
            </Popconfirm>
          </div>
        );
      }
    }
  ];

  return (
    <Card bordered={false} className={styles.fixPageContentBox}>
      <Form onFinish={onSearch} form={form} autoComplete="off">
        <Row gutter={formSearchGutter}>
          <Col span={6}>
            <Form.Item label="智能体" name="name">
              <Input placeholder="请输入智能体名称" allowClear />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="状态" name="status">
              <Select placeholder="请选择状态" allowClear>
                <Select.Option value={1}>启用</Select.Option>
                <Select.Option value={0}>禁用</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item>
              <div className={styles.searchBox}>
                <Button type="primary" htmlType="submit">
                  搜索
                </Button>
                <Button style={{ marginLeft: 8 }} onClick={onReset}>
                  重置
                </Button>
              </div>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <div className={styles.operateBox}>
        <Button
          type="primary"
          onClick={() => setOperateInfo({ type: 'add' })}
        >
          新建智能体
        </Button>
      </div>
      <CommonTable
        rowKey="id"
        columns={columns}
        dataSource={data}
        pagination={false}
        bordered
        loading={loading}
      />
      {['add', 'edit', 'detail'].includes(operateInfo.type) && (
        <OperateModal
          type={operateInfo.type}
          data={operateInfo.data}
          closeModal={() => setOperateInfo({})}
          reloadData={getData}
        />
      )}
    </Card>
  );
};

export default SmartCustomerServiceNumber;
